import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { useLanguage } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const renderInline = (text) =>
  text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith("**") && part.endsWith("**") ? (
      <strong key={i} className="font-semibold text-[#0F2645]">{part.slice(2, -2)}</strong>
    ) : (
      part
    )
  );

const ArticleBody = ({ content }) => {
  const elements = [];
  let listBuffer = [];
  const flushList = (key) => {
    if (listBuffer.length === 0) return;
    elements.push(
      <ul key={`ul-${key}`} className="space-y-2.5 pl-1">
        {listBuffer.map((item, j) => (
          <li key={j} className="flex items-start gap-3 text-base leading-relaxed text-[#5C6A87]">
            <span className="mt-2.5 w-1.5 h-1.5 rounded-full bg-[#3A5CC8] shrink-0" />
            <span>{renderInline(item)}</span>
          </li>
        ))}
      </ul>
    );
    listBuffer = [];
  };
  content.split("\n").forEach((line, i) => {
    const trimmed = line.trim();
    if (trimmed.startsWith("- ")) {
      listBuffer.push(trimmed.slice(2));
      return;
    }
    flushList(i);
    if (!trimmed) return;
    if (trimmed.startsWith("## ")) {
      elements.push(
        <h2 key={i} className="font-heading text-2xl font-semibold tracking-tight text-[#0F2645] pt-4">
          {renderInline(trimmed.slice(3))}
        </h2>
      );
    } else if (trimmed.startsWith("# ")) {
      elements.push(
        <h2 key={i} className="font-heading text-2xl font-semibold tracking-tight text-[#0F2645] pt-4">
          {renderInline(trimmed.slice(2))}
        </h2>
      );
    } else {
      elements.push(
        <p key={i} className="text-base leading-relaxed text-[#5C6A87]">
          {renderInline(trimmed)}
        </p>
      );
    }
  });
  flushList("end");
  return (
    <div data-testid="blog-article-content" className="mt-10 space-y-6">
      {elements}
    </div>
  );
};

export default function BlogPostPage() {
  const { t, lang } = useLanguage();
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setPost(null);
    setNotFound(false);
    axios
      .get(`${API}/blog/${slug}?lang=${lang}`)
      .then(({ data }) => setPost(data))
      .catch(() => setNotFound(true));
  }, [slug, lang]);

  useEffect(() => {
    if (!post) return;
    const previousTitle = document.title;
    document.title = `${post.title} | Blog Devisto`;
    const meta = document.querySelector('meta[name="description"]');
    const previousMeta = meta?.getAttribute("content");
    if (meta && post.excerpt) meta.setAttribute("content", post.excerpt);
    const jsonLd = document.createElement("script");
    jsonLd.type = "application/ld+json";
    jsonLd.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Article",
      headline: post.title,
      description: post.excerpt,
      datePublished: post.created_at,
      author: { "@type": "Organization", name: "Devisto" },
      publisher: { "@type": "Organization", name: "Devisto" },
      inLanguage: { nl: "nl-BE", es: "es-ES" }[post.lang] || "fr-BE",
    });
    document.head.appendChild(jsonLd);
    return () => {
      document.title = previousTitle;
      if (meta && previousMeta) meta.setAttribute("content", previousMeta);
      jsonLd.remove();
    };
  }, [post]);

  return (
    <div data-testid="blog-post-page" className="font-body bg-[#F6F8FD] text-[#0F2645] min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 pt-20">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 py-16 sm:py-24">
          <Link
            to="/blog"
            data-testid="blog-back-link"
            className="inline-flex items-center gap-2 text-sm font-medium text-[#5C6A87] hover:text-[#0F2645] transition-colors duration-200"
          >
            <ArrowLeft className="w-4 h-4" /> {t("Tous les guides")}
          </Link>

          {notFound ? (
            <div data-testid="blog-not-found" className="mt-12 text-center py-16">
              <h1 className="font-heading text-3xl font-bold tracking-tight text-[#0F2645]">{t("Article introuvable")}</h1>
              <p className="mt-3 text-base text-[#5C6A87]">{t("Cet article n'existe pas ou n'est plus publié.")}</p>
            </div>
          ) : !post ? (
            <p className="mt-12 text-sm text-[#5C6A87]">{t("Chargement…")}</p>
          ) : (
            <article className="mt-8">
              {post.category && (
                <span className="rounded-full bg-[#EEF1FC] border border-blue-100 px-3 py-1 text-xs font-bold uppercase tracking-wider text-[#3A5CC8]">
                  {post.category}
                </span>
              )}
              <h1 data-testid="blog-post-title" className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#0F2645]">
                {post.title}
              </h1>
              <p className="mt-4 text-sm text-[#5C6A87]">
                {t("Publié le")} {new Date(post.created_at).toLocaleDateString({ nl: "nl-BE", es: "es-ES" }[lang] || "fr-BE", { day: "numeric", month: "long", year: "numeric" })} · {t("Par l'équipe Devisto")}
              </p>
              {post.cover_image && (
                <img loading="lazy" decoding="async" src={post.cover_image} alt={post.title} className="mt-8 w-full h-72 object-cover rounded-2xl border border-[#E2E7F7]" />
              )}
              {post.excerpt && (
                <p className="mt-8 text-lg leading-relaxed text-[#0F2645] font-medium border-l-4 border-[#3A5CC8] pl-5">
                  {post.excerpt}
                </p>
              )}
              <ArticleBody content={post.content} />

              <div className="mt-14 rounded-2xl bg-[#0F2645] p-8 sm:p-10 text-center">
                <h2 className="font-heading text-2xl font-bold tracking-tight text-white">
                  {t("Un projet de travaux ?")}
                </h2>
                <p className="mt-3 text-sm sm:text-base text-slate-300 max-w-md mx-auto">
                  {t("Décrivez votre besoin gratuitement : un professionnel vérifié de votre région vous recontacte.")}
                </p>
                <Link
                  to="/publier-une-demande"
                  data-testid="blog-cta-btn"
                  className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#3A5CC8] px-8 py-4 text-base font-semibold text-white shadow-lg shadow-blue-600/25 hover:bg-[#3A5CC8] transition-colors duration-200"
                >
                  {t("Décrire mon projet")} <ArrowRight className="w-5 h-5" />
                </Link>
              </div>
            </article>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
